import { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import { APIWeb } from '@/api'
import StepIntro from './steps/StepIntro'
import StepSelectCardholderType from './steps/StepSelectCardholderType'
import StepBasicInfo from './steps/StepBasicInfo'
import StepCompleteInfo from './steps/StepCompleteInfo'
import StepUploadDocument from './steps/StepUploadDocument'
import StepUploadHandheld from './steps/StepUploadHandheld'
import StepSuccess from './steps/StepSuccess'
import { 
  KycStep, 
  KycMode, 
  type KycFormData, 
  getInitialKycFormForAllowedTypes,
  normalizeAllowedKycTypes,
  isDocumentUploadValid,
  isHandheldUploadValid,
  convertFormDataToApiRequest
} from './types'

type AddKycLocationState = {
  allowed_kyc_types?: string[] | string
}

export default function AddKyc() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locState = location.state as AddKycLocationState | null
  const allowedKycTypesFromApply = normalizeAllowedKycTypes(locState?.allowed_kyc_types)

  const [currentStep, setCurrentStep] = useState<KycStep>(KycStep.Intro)
  const [formData, setFormData] = useState<KycFormData>(() =>
    getInitialKycFormForAllowedTypes(allowedKycTypesFromApply)
  )
  const [submitting, setSubmitting] = useState(false)

  const updateFormData = useCallback((data: Partial<KycFormData>) => {
    setFormData(prev => ({ ...prev, ...data }))
  }, [])

  const goToNextStep = useCallback((step: KycStep) => {
    setCurrentStep(step)
  }, [])

  const goToPrevStep = useCallback(() => {
    switch (currentStep) {
      case KycStep.Intro:
        navigate(-1)
        break
      case KycStep.SelectCardholderType:
        setCurrentStep(KycStep.Intro)
        break
      case KycStep.BasicInfo:
        setCurrentStep(KycStep.SelectCardholderType)
        break
      case KycStep.CompleteInfo:
        setCurrentStep(KycStep.BasicInfo)
        break
      case KycStep.UploadDocument:
        setCurrentStep(KycStep.CompleteInfo)
        break
      case KycStep.UploadHandheld:
        setCurrentStep(KycStep.UploadDocument)
        break
      default:
        navigate(-1)
    }
  }, [currentStep, navigate])

  const goToCardholderList = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const canProceedDocumentUpload = useMemo(() => isDocumentUploadValid(formData), [formData])
  const canProceedHandheldUpload = useMemo(() => isHandheldUploadValid(formData), [formData])

  const handleSubmit = useCallback(async () => {
    if (!canProceedHandheldUpload) return

    try {
      setSubmitting(true)
      const requestData = convertFormDataToApiRequest(formData)
      await APIWeb.applyKyc(requestData)
      goToNextStep(KycStep.Success)
    } catch (error: any) {
      console.error('KYC申请失败:', error)
      Toast.fail(error?.message || t('提交失败请稍后重试'))
    } finally {
      setSubmitting(false)
    }
  }, [formData, canProceedHandheldUpload, goToNextStep])

  const renderStep = () => {
    switch (currentStep) {
      case KycStep.Intro:
        return (
          <StepIntro
            onNext={() => goToNextStep(KycStep.SelectCardholderType)}
            onBack={goToPrevStep}
          />
        )
      case KycStep.SelectCardholderType:
        return (
          <StepSelectCardholderType
            formData={formData}
            updateFormData={updateFormData}
            onNext={() => goToNextStep(KycStep.BasicInfo)}
            onBack={goToPrevStep}
            allowedKycTypes={allowedKycTypesFromApply}
          />
        )
      case KycStep.BasicInfo:
        return (
          <StepBasicInfo
            formData={formData}
            updateFormData={updateFormData}
            onNext={() => goToNextStep(KycStep.CompleteInfo)}
            onBack={goToPrevStep}
          />
        )
      case KycStep.CompleteInfo:
        return (
          <StepCompleteInfo
            formData={formData}
            updateFormData={updateFormData}
            onNext={() => goToNextStep(KycStep.UploadDocument)}
            onBack={goToPrevStep}
          />
        )
      case KycStep.UploadDocument:
        return (
          <StepUploadDocument
            formData={formData}
            updateFormData={updateFormData}
            onNext={() => goToNextStep(KycStep.UploadHandheld)}
            onBack={goToPrevStep}
            mode={KycMode.Add}
            canProceed={canProceedDocumentUpload}
          />
        )
      case KycStep.UploadHandheld:
        return (
          <StepUploadHandheld
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleSubmit}
            onBack={goToPrevStep}
            mode={KycMode.Add}
            canProceed={canProceedHandheldUpload}
            submitting={submitting}
          />
        )
      case KycStep.Success:
        return (
          <StepSuccess
            onGoHome={goToCardholderList}
          />
        )
      default:
        return null
    }
  }

  return (
    <PageLayout>
      {renderStep()}
    </PageLayout>
  )
}
